import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { shipmentService } from "../../services/shipmentService"
import shipmentClientService from "../../services/ShipmentClientService.js"
import { useAuth } from "../../context/AuthContext"
import Alert from "../common/Alert"
import { MapPin, ArrowLeft, ChevronDown, ChevronUp } from "lucide-react"
import { PageLayout, Button } from "../common/index.js"

const LABEL_CLS = "block text-xs font-medium text-secondary mb-1"
const INPUT_CLS =
  "w-full px-3 py-1.5 text-sm border border-gray-300 rounded-input focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 disabled:bg-gray-100 disabled:cursor-not-allowed"
const INPUT_ERROR_CLS = "border-red-300 focus:border-red-500 focus:ring-red-200"

const hasExtraPartyData = (address, email, gst) =>
  !!(String(address || "").trim() || String(email || "").trim() || String(gst || "").trim())

const isHubLocation = (locationName, hubs) =>
  hubs.some((h) => (h.location || "").trim().toLowerCase() === (locationName || "").trim().toLowerCase())

const AddShipment = () => {
  const [submittedEwayBills, setSubmittedEwayBills] = useState([])
  const [showEwayBillModal, setShowEwayBillModal] = useState(false)
  const [showGSTDetails, setShowGSTDetails] = useState(false)
  const [showAdditionalCharges, setShowAdditionalCharges] = useState(false)
  const [showConsignorExtra, setShowConsignorExtra] = useState(false)
  const [showConsigneeExtra, setShowConsigneeExtra] = useState(false)
  const [showShipmentExtra, setShowShipmentExtra] = useState(false)
  const [senderHighlightIndex, setSenderHighlightIndex] = useState(-1)
  const [receiverHighlightIndex, setReceiverHighlightIndex] = useState(-1)
  const senderLocationRef = useRef(null)
  const receiverLocationRef = useRef(null)
  const [ewayBills, setEwayBills] = useState([""])
  const isValidEway = (value) => /^\d{12}$/.test(value)
  const handleCloseEwayBillModal = () => {
    setShowEwayBillModal(false)
    setEwayBills([""]) // reset popup state
  }
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [consignerLocations, setConsignerLocations] = useState([])
  const [consigneeLocations, setConsigneeLocations] = useState([])
  const [senderLocationSearch, setSenderLocationSearch] = useState("")
  const [receiverLocationSearch, setReceiverLocationSearch] = useState("")
  const [showSenderDropdown, setShowSenderDropdown] = useState(false)
  const [showReceiverDropdown, setShowReceiverDropdown] = useState(false)

  // Customer auto-suggestions
  const [senderSuggestions, setSenderSuggestions] = useState([])
  const [showSenderSuggestions, setShowSenderSuggestions] = useState(false)
  const [receiverSuggestions, setReceiverSuggestions] = useState([])
  const [showReceiverSuggestions, setShowReceiverSuggestions] = useState(false)

  const [useShipmentClient, setUseShipmentClient] = useState(false)
  const [shipmentClients, setShipmentClients] = useState([])
  const [clientSearch, setClientSearch] = useState("")
  const [showClientDropdown, setShowClientDropdown] = useState(false)
  const [selectedClient, setSelectedClient] = useState(null)

  const [showChargesModal, setShowChargesModal] = useState(false)
  const [chargesExpanded, setChargesExpanded] = useState(false)
  const [showViewEwayBillModal, setShowViewEwayBillModal] = useState(false)
  const [showArticleDropdown, setShowArticleDropdown] = useState(false)
  const [articleSearchMode, setArticleSearchMode] = useState(false)
  const [articleHighlightIndex, setArticleHighlightIndex] = useState(-1)
  const articleTypes = ["BUNDLE","THERMOCOL BOX","ROLL","GUNNU BAGS","CARTON","CAN","AUTO PARTS","METAL","SHEET","BRIEFCASE","ENVELOP","BOX","PACKAGE","PARCEL","CRATE","DRUM","BAG","PALLET","OTHER"]

  const [formData, setFormData] = useState({
    sender_name: "",
    sender_address: "",
    sender_location: "",
    sender_contact: "",
    sender_email: "",
    sender_gst: "",
    receiver_name: "",
    receiver_address: "",
    receiver_location: "",
    receiver_contact: "",
    receiver_email: "",
    receiver_gst: "",
    invoice_number: "",
    no_of_articles: 1,
    article_type: "BOX",
    article_amount: "",
    weight: "",
    shipment_value: "",
    freight_charges: "",
    handling_charges: "",
    delivery_charges: "",
    door_pickup_charges: "",
    docket_fee: "0",
    other_charges: "",
    discount: "",
    delivery_type: "hub_delivery",
    mode_of_payment: "to_pay",
    payment_mode: "",
    pickup_type: "partial_pickup",
    parcel_detail: "",
    parcel_image_1: null,
    parcel_image_2: null,
    paid_amount: "",
    sgst_percentage: "",
    cgst_percentage: "",
    igst_percentage: "",
    custom_shipment_id: "",
    use_custom_shipment_id: false,
    shipment_id_type: "auto",
    manual_shipment_id: "",
  })

  const [imagePreview, setImagePreview] = useState({ img1: null, img2: null })
  const [shipmentIdError, setShipmentIdError] = useState("")

  useEffect(() => {
    fetchLocationHubs()
    fetchShipmentClients()

    if (user?.city) {
      console.log('Setting sender_location to:', user.city)
      setFormData(prev => ({ ...prev, sender_location: user.city }))
    }
  }, [user?.city])

  useEffect(() => {
    if (useShipmentClient || !hasExtraPartyData(formData.sender_address, formData.sender_email, formData.sender_gst)) {
      setShowConsignorExtra(false)
    }
  }, [formData.sender_address, formData.sender_email, formData.sender_gst, useShipmentClient])

  useEffect(() => {
    if (!hasExtraPartyData(formData.receiver_address, formData.receiver_email, formData.receiver_gst)) {
      setShowConsigneeExtra(false)
    }
  }, [formData.receiver_address, formData.receiver_email, formData.receiver_gst])

  const filteredSenderLocations = consignerLocations.filter((loc) =>
    loc.location.toLowerCase().includes(senderLocationSearch.toLowerCase()),
  )

  const filteredReceiverLocations = consigneeLocations.filter((loc) =>
    loc.location.toLowerCase().includes(receiverLocationSearch.toLowerCase()),
  )

  const filteredArticleTypes = articleTypes.filter(
    (t) => !articleSearchMode || !formData.article_type || t.includes(formData.article_type),
  )

  const fetchLocationHubs = async () => {
    try {
      // Fetch all locations
      const response = await shipmentService.getAllLocationHubs()
      console.log("All locations:", response.data)

      const allLocations = response.data.hubs || []
      setConsignerLocations(allLocations)
      setConsigneeLocations(allLocations)
    } catch (error) {
      console.error("Error fetching location hubs:", error)
      Alert.error("Failed to load location hubs")
    }
  }

  const calculateFreightCharges = () => {
    const articleAmount = Number.parseFloat(formData.article_amount) || 0
    const noOfArticles = Number.parseFloat(formData.no_of_articles) || 1
    return (articleAmount * noOfArticles).toFixed(2)
  }

  const calculateTotalAmount = () => {
    const freightCharges = Number.parseFloat(calculateFreightCharges()) || 0
    const handlingCharges = Number.parseFloat(formData.handling_charges) || 0
    const deliveryCharges = Number.parseFloat(formData.delivery_charges) || 0
    const doorPickupCharges = Number.parseFloat(formData.door_pickup_charges) || 0
    const otherCharges = Number.parseFloat(formData.other_charges) || 0
    const discount = Number.parseFloat(formData.discount) || 0
    const docketFee = Number.parseFloat(formData.docket_fee) || 0
    
    const subtotalBeforeDiscount = freightCharges + handlingCharges + deliveryCharges + doorPickupCharges + docketFee + otherCharges
    const subtotal = Math.max(0, subtotalBeforeDiscount - discount)
    
    // GST is calculated on freight charges only
    const sgst = (freightCharges * (Number.parseFloat(formData.sgst_percentage) || 0)) / 100
    const cgst = (freightCharges * (Number.parseFloat(formData.cgst_percentage) || 0)) / 100
    const igst = (freightCharges * (Number.parseFloat(formData.igst_percentage) || 0)) / 100
    
    // Total now includes GST
    const total = subtotal + sgst + cgst + igst
    return total.toFixed(2)
  }

  const calculateGSTAmounts = () => {
    const freightCharges = Number.parseFloat(calculateFreightCharges()) || 0
    
    // GST is calculated on freight charges only
    const sgst = (freightCharges * (Number.parseFloat(formData.sgst_percentage) || 0)) / 100
    const cgst = (freightCharges * (Number.parseFloat(formData.cgst_percentage) || 0)) / 100
    const igst = (freightCharges * (Number.parseFloat(formData.igst_percentage) || 0)) / 100
    
    return { 
      sgst: sgst.toFixed(2), 
      cgst: cgst.toFixed(2), 
      igst: igst.toFixed(2) 
    }
  }

  const handleChange = (e) => {
    const { name, value, type } = e.target

    // Validate manual_shipment_id - no special characters allowed
    if (name === "manual_shipment_id") {
      if (/[^a-zA-Z0-9]/.test(value)) {
        setShipmentIdError("Shipment ID cannot contain special characters")
      } else {
        setShipmentIdError("")
      }
      setFormData(prev => ({ ...prev, manual_shipment_id: value.replace(/[^a-zA-Z0-9]/g, "") }))
      return
    }

    const chargeFields = [
      "freight_charges",
      "handling_charges",
      "delivery_charges",
      "door_pickup_charges",
      "other_charges",
      "docket_fee",
      "discount",
      "paid_amount",
      "article_amount",
      "sgst_percentage",
      "cgst_percentage",
      "igst_percentage",
    ]
    if (chargeFields.includes(name)) {
      if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
        // Special validation for paid_amount
        if (name === "paid_amount" && value !== "") {
          const paidAmount = parseFloat(value)
          const totalAmount = parseFloat(calculateTotalAmount())
          
          if (paidAmount > totalAmount) {
            Alert.error(`Paid amount (₹${paidAmount.toFixed(2)}) cannot exceed total amount (₹${totalAmount.toFixed(2)})`)
            return
          }
        }
        setFormData({ ...formData, [name]: value })
      }
    } else {
      const updatedFormData = {
        ...formData,
        [name]: type === "number" ? (value === "" ? "" : Number.parseFloat(value)) : value,
      }

      // Handle payment status changes
      if (name === "mode_of_payment") {
        if (value === "to_pay" || value === "credit") {
          updatedFormData.paid_amount = ""
          updatedFormData.payment_mode = ""
        } else if (value === "paid" || value === "paid_balance") {
          updatedFormData.payment_mode = "cash"
          updatedFormData.paid_amount = calculateTotalAmount()
        } else if (value === "foc") {
          updatedFormData.article_amount = "0"
          updatedFormData.handling_charges = "0"
          updatedFormData.delivery_charges = "0"
          updatedFormData.door_pickup_charges = "0"
          updatedFormData.docket_fee = "0"
          updatedFormData.other_charges = "0"
          updatedFormData.discount = "0"
          updatedFormData.sgst_percentage = "0"
          updatedFormData.cgst_percentage = "0"
          updatedFormData.igst_percentage = "0"
          updatedFormData.paid_amount = "0"
          updatedFormData.payment_mode = "foc"
        }
      }

      // Handle payment mode selection - auto-fill total for "paid" status only
      if (name === "payment_mode") {
        if (formData.mode_of_payment === "paid" && value !== "") {
          updatedFormData.paid_amount = calculateTotalAmount()
        }
      }

      setFormData(updatedFormData)
    }
  }

  const handleSubmitEwayBills = () => {
    const validBills = ewayBills.filter(isValidEway)
    if (validBills.length === 0) {
      Alert.error("Please enter at least one valid E-way bill")
      return
    }
    
    // Check for duplicates
    const allBills = [...submittedEwayBills, ...validBills]
    const uniqueBills = [...new Set(allBills)]
    
    if (uniqueBills.length !== allBills.length) {
      Alert.error("Duplicate E-way bill numbers found")
      return
    }
    
    setSubmittedEwayBills(uniqueBills)
    Alert.success(`${validBills.length} E-way bill(s) added successfully`)
    handleCloseEwayBillModal()
  }

  const handleViewEwayBills = () => {
    if (submittedEwayBills.length === 0) {
      Alert.info("No E-way bills added yet")
      return
    }
    setShowViewEwayBillModal(true)
  }

  const handleRemoveEwayBill = (indexToRemove) => {
    const updated = submittedEwayBills.filter((_, index) => index !== indexToRemove)
    setSubmittedEwayBills(updated)
    if (updated.length === 0) {
      setShowViewEwayBillModal(false)
    }
    Alert.success("E-way bill removed successfully")
  }

  const fetchShipmentClients = async () => {
    try {
      const response = await shipmentClientService.getAll(1, 1000, { status: "active" })
      setShipmentClients(response.data || [])
    } catch (error) {
      console.error("Error fetching shipment clients:", error)
      Alert.error("Failed to load shipment clients")
    }
  }

  const isAgentRole = user?.role === "Agent"

  const fetchCustomerSuggestions = async (type, name = "", contact = "") => {
    if (isAgentRole) return
    if (!name && !contact) {
      type === "sender" ? setSenderSuggestions([]) : setReceiverSuggestions([])
      return
    }
    try {
      const res = await shipmentService.getCustomerSuggestions(name, contact)
      // API returns: { data: { senders: [...], receivers: [...] }, success: true }
      const payload = res.data?.data || res.data || {}
      const suggestions = type === "sender"
        ? (Array.isArray(payload.senders) ? payload.senders : [])
        : (Array.isArray(payload.receivers) ? payload.receivers : [])
      type === "sender" ? setSenderSuggestions(suggestions) : setReceiverSuggestions(suggestions)
    } catch (err) {
      console.error("Error fetching customer suggestions:", err)
    }
  }

  const handleSelectSuggestion = (type, suggestion) => {
    const prefix = type === "sender" ? "sender" : "receiver"
    setFormData((prev) => ({
      ...prev,
      [`${prefix}_name`]: suggestion.name || "",
      [`${prefix}_contact`]: suggestion.contact || "",
      [`${prefix}_address`]: suggestion.address || "",
      [`${prefix}_email`]: suggestion.email || "",
      [`${prefix}_gst`]: suggestion.gst || "",
    }))
    if (type === "sender") {
      setShowConsignorExtra(false)
      setSenderSuggestions([])
      setShowSenderSuggestions(false)
    } else {
      setShowConsigneeExtra(false)
      setReceiverSuggestions([])
      setShowReceiverSuggestions(false)
    }
  }

  const filteredClients = shipmentClients.filter(
    (client) =>
      client.full_name.toLowerCase().includes(clientSearch.toLowerCase()) ||
      client.company_name.toLowerCase().includes(clientSearch.toLowerCase()),
  )

  const handleClientSelect = (client) => {
    setSelectedClient(client)
    setShowConsignorExtra(false)
    setFormData({
      ...formData,
      sender_name: client.full_name,
      sender_address: client.address,
      sender_contact: client.contact,
      sender_email: client.email,
      sender_gst: client.gst_number || "",
      shipment_client_id: client.id,
    })
    setClientSearch("")
    setShowClientDropdown(false)
  }

  const handleUseClientChange = (checked) => {
    setUseShipmentClient(checked)
    if (checked) {
      setShowConsignorExtra(false)
    }
    if (!checked) {
      // Clear client selection
      setSelectedClient(null)
      setFormData({
        ...formData,
        sender_name: "",
        sender_address: "",
        sender_contact: "",
        sender_email: "",
        sender_gst: "",
        shipment_client_id: null,
      })
    }
  }

  const handleImageChange = (e) => {
    const { name } = e.target
    const file = e.target.files?.[0]
    if (file) {
      setFormData({ ...formData, [name]: file })
      const reader = new FileReader()
      reader.onload = (e) => {
        if (name === "parcel_image_1") {
          setImagePreview({ ...imagePreview, img1: e.target.result })
        } else {
          setImagePreview({ ...imagePreview, img2: e.target.result })
        }
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    // Validate locations
    if (!formData.sender_location || formData.sender_location.trim() === "") {
      Alert.error("Please select a sender location from the dropdown")
      return
    }
    if (!isHubLocation(formData.sender_location, consignerLocations)) {
      Alert.error("Please select a valid From Destination from the dropdown list")
      return
    }
    if (!formData.receiver_location || formData.receiver_location.trim() === "") {
      Alert.error("Please select a receiver location from the dropdown")
      return
    }
    if (!isHubLocation(formData.receiver_location, consigneeLocations)) {
      Alert.error("Please select a valid To Destination from the dropdown list")
      return
    }
    const phoneRegex = /^[0-9]\d{9}$/
    if (!phoneRegex.test(formData.sender_contact)) {
      Alert.error("Sender contact must be a valid 10-digit mobile number")
      setLoading(false)
      return
    }
    if (!phoneRegex.test(formData.receiver_contact)) {
      Alert.error("Receiver contact must be a valid 10-digit mobile number")
      setLoading(false)
      return
    }
    try {
      setLoading(true)

      const freightCharges = Number.parseFloat(calculateFreightCharges()) || 0
      // const deliveryCharges =
      //   formData.delivery_type === "door_delivery" ? Number.parseFloat(formData.delivery_charges) || 0 : 0

      // Validate paid amount doesn't exceed total (moved before submitData creation)
      if ((formData.mode_of_payment === 'paid' || formData.mode_of_payment === 'paid_balance')) {
        if (!formData.payment_mode) {
          Alert.error("Please select a payment mode")
          setLoading(false)
          return
        }

        const paidAmount = Number.parseFloat(formData.paid_amount) || 0
        const totalAmount = Number.parseFloat(calculateTotalAmount())
        
        if (paidAmount > totalAmount) {
          Alert.error(`Paid amount (₹${paidAmount.toFixed(2)}) cannot exceed total amount (₹${totalAmount.toFixed(2)})`)
          setLoading(false)
          return
        }
      }

      // Validate shipment ID fields
      if (formData.shipment_id_type === "manual") {
        if (!formData.manual_shipment_id || formData.manual_shipment_id.trim() === "") {
          Alert.error("Please enter a manual shipment ID")
          setLoading(false)
          return
        }
      } else if (formData.use_custom_shipment_id) {
        if (!formData.custom_shipment_id || formData.custom_shipment_id.trim() === "") {
          Alert.error("Please enter a custom shipment ID suffix")
          setLoading(false)
          return
        }
      }

      const submitData = {
        ...formData,
        eway_bill_numbers: submittedEwayBills,
        no_of_articles: Number.parseFloat(formData.no_of_articles) || 1,
        article_amount: Number.parseFloat(formData.article_amount) || 0,
        freight_charges: freightCharges,
        handling_charges: Number.parseFloat(formData.handling_charges) || 0,
        delivery_charges: Number.parseFloat(formData.delivery_charges) || 0,
        door_pickup_charges: Number.parseFloat(formData.door_pickup_charges) || 0,
        docket_fee: Number.parseFloat(formData.docket_fee) || 0,
        other_charges: Number.parseFloat(formData.other_charges) || 0,
        discount: Number.parseFloat(formData.discount) || 0,
        sgst_percentage: Number.parseFloat(formData.sgst_percentage) || 0,
        cgst_percentage: Number.parseFloat(formData.cgst_percentage) || 0,
        igst_percentage: Number.parseFloat(formData.igst_percentage) || 0,
        paid_amount: Number.parseFloat(formData.paid_amount) || 0,
        payment_mode: formData.payment_mode || null,
        client_id: user?.user_id,
        client_role: user?.role,
        created_by: user?.user_id,
        shipment_id_type: formData.shipment_id_type,
        manual_shipment_id: formData.shipment_id_type === "manual" ? formData.manual_shipment_id.trim() : null,
        custom_shipment_id: formData.use_custom_shipment_id ? formData.custom_shipment_id.trim() : null,
        use_custom_shipment_id: formData.use_custom_shipment_id,
      }
      await shipmentService.createShipment(submitData)
      Alert.success("Shipment created successfully")
      navigate("/shipments")
        } catch (error) {
      console.error("Error creating shipment:", error)
      let errorMsg = error.response?.data?.error || "Failed to create shipment"
      
      // Detect duplicate shipment ID or invoice number and show clean alert
      if (
        errorMsg.includes("already exists") ||
        errorMsg.toLowerCase().includes("duplicate") ||
        errorMsg.toLowerCase().includes("unique constraint") ||
        errorMsg.includes("Shipment ID")
      ) {
        errorMsg = "❌ Shipment ID or Invoice Number already exists!\n\nPlease use a different custom suffix or leave Invoice Number blank."
      }
      
      Alert.error(errorMsg)
    } finally {
      setLoading(false)
    }
  }

  const calculateBalanceAmount = () => {
    const totalAmount = Number.parseFloat(calculateTotalAmount())
    const paid = Number.parseFloat(formData.paid_amount) || 0
    return (totalAmount - paid).toFixed(2)
  }

  const focusFieldByName = (name) => {
    const el = document.querySelector(`#add-shipment-form [name="${name}"]`)
    if (el && !el.disabled) el.focus()
  }

  const getFormFocusables = () => {
    const form = document.getElementById("add-shipment-form")
    if (!form) return []
    const elements = Array.from(
      form.querySelectorAll(
        "input:not([type=hidden]):not([disabled]):not([type=checkbox]):not([type=radio]):not([type=file]), select:not([disabled]), textarea:not([disabled])"
      )
    ).filter((el) => el.offsetParent !== null)
    const submitBtn = document.querySelector('button[form="add-shipment-form"][type="submit"]')
    if (submitBtn) elements.push(submitBtn)
    return elements
  }

  const focusNextField = (currentEl) => {
    const elements = getFormFocusables()
    const index = elements.indexOf(currentEl)
    if (index > -1 && index + 1 < elements.length) {
      elements[index + 1].focus()
    }
  }

  const handleEnterAsTab = (e) => {
    if (e.key !== "Enter") return
    e.preventDefault()
    if (e.target.type === "submit" || e.target.getAttribute("form") === "add-shipment-form") {
      document.getElementById("add-shipment-form")?.requestSubmit()
      return
    }
    focusNextField(e.target)
  }

  const handleFieldEnter = (e, nextFieldName) => {
    if (e.key !== "Enter") return
    if (showSenderSuggestions || showReceiverSuggestions || showClientDropdown || showArticleDropdown) return
    e.preventDefault()
    if (nextFieldName) {
      focusFieldByName(nextFieldName)
    } else {
      focusNextField(e.target)
    }
  }

  const selectSenderLocation = (loc) => {
    if (loc.location === formData.receiver_location && loc.location !== "") {
      Alert.error("Sender and Receiver locations cannot be the same")
      return
    }
    setFormData({ ...formData, sender_location: loc.location })
    setSenderLocationSearch("")
    setShowSenderDropdown(false)
    setSenderHighlightIndex(-1)
  }

  const selectReceiverLocation = (loc) => {
    if (loc.location === formData.sender_location && loc.location !== "") {
      Alert.error("Sender and Receiver locations cannot be the same")
      return
    }
    setFormData({ ...formData, receiver_location: loc.location })
    setReceiverLocationSearch("")
    setShowReceiverDropdown(false)
    setReceiverHighlightIndex(-1)
  }

  const selectArticleType = (type) => {
    setFormData((prev) => ({ ...prev, article_type: type }))
    setShowArticleDropdown(false)
    setArticleSearchMode(false)
    setArticleHighlightIndex(-1)
  }

  const handleArticleTypeKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      if (!showArticleDropdown) {
        setShowArticleDropdown(true)
        setArticleSearchMode(false)
      }
      if (filteredArticleTypes.length > 0) {
        setArticleHighlightIndex((prev) => (prev + 1) % filteredArticleTypes.length)
      }
      return
    }
    if (e.key === "ArrowUp") {
      e.preventDefault()
      if (!showArticleDropdown) {
        setShowArticleDropdown(true)
        setArticleSearchMode(false)
      }
      if (filteredArticleTypes.length > 0) {
        setArticleHighlightIndex((prev) => (prev <= 0 ? filteredArticleTypes.length - 1 : prev - 1))
      }
      return
    }
    if (e.key === "Escape") {
      if (showArticleDropdown) {
        e.preventDefault()
        setShowArticleDropdown(false)
        setArticleHighlightIndex(-1)
      }
      return
    }
    if (e.key === "Enter") {
      if (showArticleDropdown && filteredArticleTypes.length > 0 && articleHighlightIndex >= 0) {
        e.preventDefault()
        selectArticleType(filteredArticleTypes[articleHighlightIndex])
        setTimeout(() => focusNextField(e.target), 0)
        return
      }
    }
    handleEnterAsTab(e)
  }

  const handleLocationBlur = (type) => {
    const isSender = type === "sender"
    const hubs = isSender ? consignerLocations : consigneeLocations
    const selected = isSender ? formData.sender_location : formData.receiver_location
    const search = isSender ? senderLocationSearch : receiverLocationSearch
    const setSearch = isSender ? setSenderLocationSearch : setReceiverLocationSearch
    const setLocation = (loc) => (isSender ? selectSenderLocation(loc) : selectReceiverLocation(loc))

    if (selected && isHubLocation(selected, hubs)) return

    const query = search.trim()
    if (!query) {
      if (isSender) setFormData((prev) => ({ ...prev, sender_location: "" }))
      else setFormData((prev) => ({ ...prev, receiver_location: "" }))
      return
    }

    const match = hubs.find((h) => (h.location || "").trim().toLowerCase() === query.toLowerCase())
    if (match) {
      setLocation(match)
    } else {
      setSearch("")
      if (isSender) setFormData((prev) => ({ ...prev, sender_location: "" }))
      else setFormData((prev) => ({ ...prev, receiver_location: "" }))
    }
  }

  const handleLocationDropdownKeyDown = (e, { isOpen, options, highlightIndex, setHighlightIndex, onSelect, onClose, isLocationSelected }) => {
    if (e.key === "Enter" && !isLocationSelected && (!isOpen || options.length === 0 || highlightIndex < 0)) {
      e.preventDefault()
      Alert.error("Please select a location from the dropdown")
      return
    }
    if (!isOpen || options.length === 0) {
      if (e.key === "Enter") {
        e.preventDefault()
        if (!isLocationSelected) {
          Alert.error("Please select a location from the dropdown")
          return
        }
        focusNextField(e.target)
      }
      return
    }
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setHighlightIndex((prev) => (prev + 1) % options.length)
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setHighlightIndex((prev) => (prev <= 0 ? options.length - 1 : prev - 1))
    } else if (e.key === "Enter") {
      e.preventDefault()
      if (highlightIndex >= 0 && highlightIndex < options.length) {
        onSelect(options[highlightIndex])
        setTimeout(() => focusNextField(e.target), 0)
      } else if (isLocationSelected) {
        onClose()
        focusNextField(e.target)
      } else {
        Alert.error("Please select a location from the dropdown")
      }
    } else if (e.key === "Escape") {
      e.preventDefault()
      onClose()
    }
  }

  const hasConsignorExtraData =
    !useShipmentClient && hasExtraPartyData(formData.sender_address, formData.sender_email, formData.sender_gst)
  const hasConsigneeExtraData = hasExtraPartyData(
    formData.receiver_address,
    formData.receiver_email,
    formData.receiver_gst,
  )

  return (
    <PageLayout
      title="Add New Shipment"
      icon={MapPin}
      maxWidth="max-w-[100%]"
      className="!p-2 sm:!p-3 lg:!p-4 [&>div>div:first-child]:mb-2"
      actions={
        <>
          <Button variant="secondary" type="button" onClick={() => navigate("/shipments")}>
            Cancel
          </Button>
          <Button variant="secondary" icon={ArrowLeft} type="button" onClick={() => navigate("/shipments")} className="hidden sm:inline-flex">
            Back to Shipments
          </Button>
        </>
      }
    >
      <form id="add-shipment-form" onSubmit={(e) => { e.preventDefault(); handleSubmit(e) }} className="min-w-0 pb-16 space-y-2">
        {/* Destination & Documents */}
        <div className="bg-white rounded-card shadow-card p-3 min-w-0">
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-2 mb-2 pb-2 border-b border-gray-200">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 min-w-0">
              <h2 className="text-sm font-semibold text-secondary shrink-0">Destination & Documents</h2>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-600">Shipment ID:</span>
                {[
                  { value: "auto", label: "Auto" },
                  { value: "manual", label: "Manual" },
                ].map((opt) => (
                  <label key={opt.value} className="flex items-center gap-1 text-xs text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name="shipment_id_mode"
                      value={opt.value}
                      checked={opt.value === "manual" ? formData.shipment_id_type === "manual" : formData.shipment_id_type === "auto"}
                      onChange={() => {
                        if (opt.value === "manual") {
                          setFormData((prev) => ({ ...prev, shipment_id_type: "manual", use_custom_shipment_id: false, custom_shipment_id: "", manual_shipment_id: "" }))
                        } else {
                          setFormData((prev) => ({ ...prev, shipment_id_type: "auto", use_custom_shipment_id: false, custom_shipment_id: "", manual_shipment_id: "" }))
                        }
                      }}
                      className="w-3.5 h-3.5 text-brand focus:ring-brand border-gray-300"
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
                {formData.shipment_id_type === "manual" && (
                  <input
                    type="text"
                    name="manual_shipment_id"
                    placeholder="Enter shipment ID"
                    value={formData.manual_shipment_id}
                    onChange={handleChange}
                    onKeyDown={handleEnterAsTab}
                    maxLength={30}
                    className={`${INPUT_CLS} !w-36 ${shipmentIdError ? INPUT_ERROR_CLS : ""}`}
                  />
                )}
              </div>
            </div>
            <div className="flex flex-row gap-2 shrink-0">
              <Button variant="secondary" type="button" tabIndex={-1} size="sm" onClick={() => setShowEwayBillModal(true)}>
                Add E-way ID
              </Button>
              <Button variant={submittedEwayBills.length > 0 ? "primary" : "secondary"} type="button" tabIndex={-1} size="sm" onClick={handleViewEwayBills}>
                View Eway Bill {submittedEwayBills.length > 0 && `(${submittedEwayBills.length})`}
              </Button>
            </div>
          </div>
          {formData.shipment_id_type === "auto" && (
            <p className="text-[11px] text-gray-500 italic mb-2">ID will be auto-generated with location prefix</p>
          )}
          {shipmentIdError && <p className="text-xs text-red-500 mb-2">{shipmentIdError}</p>}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* From Destination */}
            <div className="flex flex-col relative" ref={senderLocationRef}>
              <label className={LABEL_CLS}>From Destination <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="sender_location_input"
                value={formData.sender_location || senderLocationSearch}
                onChange={(e) => {
                  setSenderLocationSearch(e.target.value)
                  setFormData({ ...formData, sender_location: "" })
                  setShowSenderDropdown(true)
                  setSenderHighlightIndex(-1)
                }}
                onKeyDown={(e) =>
                  handleLocationDropdownKeyDown(e, {
                    isOpen: showSenderDropdown,
                    options: filteredSenderLocations,
                    highlightIndex: senderHighlightIndex,
                    setHighlightIndex: setSenderHighlightIndex,
                    onSelect: selectSenderLocation,
                    onClose: () => { setShowSenderDropdown(false); setSenderLocationSearch(""); setSenderHighlightIndex(-1) },
                    isLocationSelected: !!formData.sender_location && isHubLocation(formData.sender_location, consignerLocations),
                  })
                }
                onFocus={() => { setShowSenderDropdown(true); setSenderHighlightIndex(-1) }}
                onBlur={() => setTimeout(() => { setShowSenderDropdown(false); setSenderHighlightIndex(-1); handleLocationBlur("sender") }, 150)}
                placeholder="Search location"
                autoComplete="off"
                className={`${INPUT_CLS} ${formData.sender_location ? "" : INPUT_ERROR_CLS}`}
              />
              {showSenderDropdown && (
                <div className="absolute z-30 w-full mt-1 top-full bg-white border border-gray-300 rounded-input shadow-lg max-h-40 overflow-y-auto">
                  {filteredSenderLocations.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-gray-400 text-center">No locations found</div>
                  ) : (
                    filteredSenderLocations.map((loc, idx) => (
                      <div
                        key={loc.hub_id || loc.id || idx}
                        onMouseEnter={() => setSenderHighlightIndex(idx)}
                        onMouseDown={() => selectSenderLocation(loc)}
                        className={`px-3 py-1.5 cursor-pointer text-sm border-b border-gray-100 last:border-b-0 ${idx === senderHighlightIndex ? "bg-brand-light text-brand font-medium" : "hover:bg-blue-50"}`}
                      >
                        {loc.location}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* To Destination */}
            <div className="flex flex-col relative" ref={receiverLocationRef}>
              <label className={LABEL_CLS}>To Destination <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="receiver_location_input"
                value={formData.receiver_location || receiverLocationSearch}
                onChange={(e) => {
                  setReceiverLocationSearch(e.target.value)
                  setFormData({ ...formData, receiver_location: "" })
                  setShowReceiverDropdown(true)
                  setReceiverHighlightIndex(-1)
                }}
                onKeyDown={(e) =>
                  handleLocationDropdownKeyDown(e, {
                    isOpen: showReceiverDropdown,
                    options: filteredReceiverLocations,
                    highlightIndex: receiverHighlightIndex,
                    setHighlightIndex: setReceiverHighlightIndex,
                    onSelect: selectReceiverLocation,
                    onClose: () => { setShowReceiverDropdown(false); setReceiverLocationSearch(""); setReceiverHighlightIndex(-1) },
                    isLocationSelected: !!formData.receiver_location && isHubLocation(formData.receiver_location, consigneeLocations),
                  })
                }
                onFocus={() => { setShowReceiverDropdown(true); setReceiverHighlightIndex(-1) }}
                onBlur={() => setTimeout(() => { setShowReceiverDropdown(false); setReceiverHighlightIndex(-1); handleLocationBlur("receiver") }, 150)}
                placeholder="Search location"
                autoComplete="off"
                className={`${INPUT_CLS} ${formData.receiver_location ? "" : INPUT_ERROR_CLS}`}
              />
              {showReceiverDropdown && (
                <div className="absolute z-30 w-full mt-1 top-full bg-white border border-gray-300 rounded-input shadow-lg max-h-40 overflow-y-auto">
                  {filteredReceiverLocations.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-gray-400 text-center">No locations found</div>
                  ) : (
                    filteredReceiverLocations.map((loc, idx) => (
                      <div
                        key={loc.hub_id || loc.id || idx}
                        onMouseEnter={() => setReceiverHighlightIndex(idx)}
                        onMouseDown={() => selectReceiverLocation(loc)}
                        className={`px-3 py-1.5 cursor-pointer text-sm border-b border-gray-100 last:border-b-0 ${idx === receiverHighlightIndex ? "bg-brand-light text-brand font-medium" : "hover:bg-blue-50"}`}
                      >
                        {loc.location}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-col">
              <label className={LABEL_CLS}>Invoice Number</label>
              <input
                type="text"
                name="invoice_number"
                placeholder="Optional"
                value={formData.invoice_number}
                onChange={handleChange}
                onKeyDown={(e) => handleFieldEnter(e, useShipmentClient ? (selectedClient ? "receiver_name" : "client_search") : "sender_name")}
                className={INPUT_CLS}
              />
            </div>
          </div>
        </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-2 min-w-0">
                  {/* Left column */}
                  <div className="space-y-2 min-w-0">
                    <div className="bg-white rounded-card shadow-card p-3">
                      <div className="flex items-center justify-between gap-2 mb-2 pb-2 border-b border-gray-200">
                        <h2 className="text-sm font-semibold text-secondary">Consignor Information</h2>
                        <div className="flex items-center gap-2 shrink-0">
                          <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                            <input type="checkbox" id="useShipmentClient" checked={useShipmentClient} onChange={(e) => handleUseClientChange(e.target.checked)} tabIndex={-1} className="w-3.5 h-3.5 text-brand border-gray-300 rounded" />
                            Existing client
                          </label>
                          {hasConsignorExtraData && (
                            <button type="button" tabIndex={-1} onClick={() => setShowConsignorExtra(!showConsignorExtra)} className="p-1 rounded-input hover:bg-gray-100 transition" aria-label="More consignor details">
                              {showConsignorExtra ? <ChevronUp className="w-4 h-4 text-secondary" /> : <ChevronDown className="w-4 h-4 text-secondary" />}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="flex flex-col relative">
                          <label className={LABEL_CLS}>Consignor Name <span className="text-red-500">*</span></label>
                          {useShipmentClient ? (
                            <>
                              <input
                                type="text"
                                name="client_search"
                                value={selectedClient ? `${selectedClient.full_name} (${selectedClient.company_name})` : clientSearch}
                                onChange={(e) => { setClientSearch(e.target.value); setSelectedClient(null); setShowClientDropdown(true) }}
                                onKeyDown={(e) => handleFieldEnter(e, "sender_contact")}
                                onFocus={() => setShowClientDropdown(true)}
                                placeholder="Search client"
                                autoComplete="off"
                                className={INPUT_CLS}
                                required
                              />
                              {showClientDropdown && filteredClients.length > 0 && !selectedClient && (
                                <div className="absolute z-20 w-full mt-1 top-full bg-white border border-gray-300 rounded-input shadow-lg max-h-40 overflow-y-auto">
                                  {filteredClients.map((client) => (
                                    <div key={client.id} onMouseDown={() => handleClientSelect(client)} className="px-3 py-1.5 hover:bg-blue-50 cursor-pointer text-sm border-b border-gray-100 last:border-b-0">
                                      <div className="font-medium">{client.full_name}</div>
                                      <div className="text-xs text-gray-600">{client.company_name}</div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="relative">
                              <input
                                type="text"
                                name="sender_name"
                                placeholder="Full Name"
                                value={formData.sender_name}
                                onChange={(e) => { handleChange(e); fetchCustomerSuggestions("sender", e.target.value, ""); setShowSenderSuggestions(true) }}
                                onKeyDown={(e) => handleFieldEnter(e, "sender_contact")}
                                onBlur={() => setTimeout(() => setShowSenderSuggestions(false), 150)}
                                autoComplete="off"
                                required
                                className={INPUT_CLS}
                              />
                              {showSenderSuggestions && senderSuggestions.length > 0 && (
                                <div className="absolute z-20 w-full top-full mt-1 bg-white border border-gray-300 rounded-input shadow-lg max-h-40 overflow-y-auto">
                                  {senderSuggestions.map((s, i) => (
                                    <div key={i} onMouseDown={() => handleSelectSuggestion("sender", s)} className="px-3 py-1.5 hover:bg-blue-50 cursor-pointer text-sm border-b border-gray-100 last:border-b-0">
                                      <span className="font-medium">{s.name}</span>
                                      <span className="text-gray-500 ml-2">— {s.contact}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col">
                          <label className={LABEL_CLS}>Contact Number <span className="text-red-500">*</span></label>
                          <div className="relative">
                            <input
                              type="text"
                              name="sender_contact"
                              placeholder="10 digit mobile"
                              value={formData.sender_contact}
                              onChange={(e) => { handleChange(e); if (!useShipmentClient) { fetchCustomerSuggestions("sender", "", e.target.value); setShowSenderSuggestions(true) } }}
                              onKeyDown={(e) => handleFieldEnter(e, "receiver_name")}
                              onBlur={() => setTimeout(() => setShowSenderSuggestions(false), 150)}
                              disabled={useShipmentClient && selectedClient}
                              autoComplete="off"
                              required
                              className={INPUT_CLS}
                            />
                          </div>
                        </div>
                      </div>
                      {showConsignorExtra && hasConsignorExtraData && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2 pt-2 border-t border-gray-100">
                          {formData.sender_address?.trim() && (
                            <div className="flex flex-col sm:col-span-2">
                              <label className={LABEL_CLS}>Address</label>
                              <input type="text" name="sender_address" value={formData.sender_address} readOnly className={`${INPUT_CLS} bg-gray-50`} />
                            </div>
                          )}
                          {formData.sender_email?.trim() && (
                            <div className="flex flex-col">
                              <label className={LABEL_CLS}>Email</label>
                              <input type="email" name="sender_email" value={formData.sender_email} readOnly className={`${INPUT_CLS} bg-gray-50`} />
                            </div>
                          )}
                          {formData.sender_gst?.trim() && (
                            <div className="flex flex-col">
                              <label className={LABEL_CLS}>GST Number</label>
                              <input type="text" name="sender_gst" value={formData.sender_gst} readOnly className={`${INPUT_CLS} bg-gray-50`} />
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Consignee Information */}
                    <div className="bg-white rounded-card shadow-card p-3">
                      <div className="flex items-center justify-between gap-2 mb-2 pb-2 border-b border-gray-200">
                        <h2 className="text-sm font-semibold text-secondary">Consignee Information</h2>
                        {hasConsigneeExtraData && (
                          <button type="button" tabIndex={-1} onClick={() => setShowConsigneeExtra(!showConsigneeExtra)} className="p-1 rounded-input hover:bg-gray-100 transition" aria-label="More consignee details">
                            {showConsigneeExtra ? <ChevronUp className="w-4 h-4 text-secondary" /> : <ChevronDown className="w-4 h-4 text-secondary" />}
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="flex flex-col">
                          <label className={LABEL_CLS}>Consignee Name <span className="text-red-500">*</span></label>
                          <div className="relative">
                            <input
                              type="text"
                              name="receiver_name"
                              value={formData.receiver_name}
                              onChange={(e) => { handleChange(e); fetchCustomerSuggestions("receiver", e.target.value, ""); setShowReceiverSuggestions(true) }}
                              onKeyDown={(e) => handleFieldEnter(e, "receiver_contact")}
                              onBlur={() => setTimeout(() => setShowReceiverSuggestions(false), 150)}
                              autoComplete="off"
                              required
                              className={INPUT_CLS}
                            />
                            {showReceiverSuggestions && receiverSuggestions.length > 0 && (
                              <div className="absolute z-20 w-full top-full mt-1 bg-white border border-gray-300 rounded-input shadow-lg max-h-40 overflow-y-auto">
                                {receiverSuggestions.map((s, i) => (
                                  <div key={i} onMouseDown={() => handleSelectSuggestion("receiver", s)} className="px-3 py-1.5 hover:bg-blue-50 cursor-pointer text-sm border-b border-gray-100 last:border-b-0">
                                    <span className="font-medium">{s.name}</span>
                                    <span className="text-gray-500 ml-2">— {s.contact}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col">
                          <label className={LABEL_CLS}>Contact Number <span className="text-red-500">*</span></label>
                          <div className="relative">
                            <input
                              type="text"
                              name="receiver_contact"
                              value={formData.receiver_contact}
                              onChange={(e) => { handleChange(e); fetchCustomerSuggestions("receiver", "", e.target.value); setShowReceiverSuggestions(true) }}
                              onKeyDown={(e) => handleFieldEnter(e, "article_type")}
                              onBlur={() => setTimeout(() => setShowReceiverSuggestions(false), 150)}
                              autoComplete="off"
                              required
                              className={INPUT_CLS}
                            />
                          </div>
                        </div>
                      </div>
                      {showConsigneeExtra && hasConsigneeExtraData && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2 pt-2 border-t border-gray-100">
                          {formData.receiver_address?.trim() && (
                            <div className="flex flex-col sm:col-span-2">
                              <label className={LABEL_CLS}>Address</label>
                              <input type="text" name="receiver_address" value={formData.receiver_address} readOnly className={`${INPUT_CLS} bg-gray-50`} />
                            </div>
                          )}
                          {formData.receiver_email?.trim() && (
                            <div className="flex flex-col">
                              <label className={LABEL_CLS}>Email</label>
                              <input type="email" name="receiver_email" value={formData.receiver_email} readOnly className={`${INPUT_CLS} bg-gray-50`} />
                            </div>
                          )}
                          {formData.receiver_gst?.trim() && (
                            <div className="flex flex-col">
                              <label className={LABEL_CLS}>GST Number</label>
                              <input type="text" name="receiver_gst" value={formData.receiver_gst} readOnly className={`${INPUT_CLS} bg-gray-50`} />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right column — Shipment & Billing */}
                  <div className="space-y-2 min-w-0">
                    <div className="bg-white rounded-card shadow-card p-3">
                      <div className="flex items-center justify-between gap-2 mb-2 pb-2 border-b border-gray-200">
                        <h2 className="text-sm font-semibold text-secondary">Shipment Details</h2>
                        <button type="button" tabIndex={-1} onClick={() => setShowShipmentExtra(!showShipmentExtra)} className="p-1 rounded-input hover:bg-gray-100 transition" aria-label="More shipment details">
                          {showShipmentExtra ? <ChevronUp className="w-4 h-4 text-secondary" /> : <ChevronDown className="w-4 h-4 text-secondary" />}
                        </button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="flex flex-col relative">
                          <label className={LABEL_CLS}>Article Type <span className="text-red-500">*</span></label>
                          <div className="relative flex items-center">
                            <input
                              type="text"
                              name="article_type"
                              placeholder="TYPE OR SELECT"
                              value={formData.article_type}
                              onChange={(e) => {
                                setFormData((prev) => ({ ...prev, article_type: e.target.value.toUpperCase() }))
                                setArticleSearchMode(true)
                                setShowArticleDropdown(true)
                                setArticleHighlightIndex(-1)
                              }}
                              onFocus={() => { setArticleSearchMode(false); setShowArticleDropdown(true); setArticleHighlightIndex(-1) }}
                              onBlur={() => setTimeout(() => { setShowArticleDropdown(false); setArticleSearchMode(false); setArticleHighlightIndex(-1) }, 150)}
                              onKeyDown={handleArticleTypeKeyDown}
                              required
                              className={`${INPUT_CLS} pr-8 uppercase`}
                            />
                            <button type="button" tabIndex={-1} onMouseDown={(e) => { e.preventDefault(); setArticleSearchMode(false); setArticleHighlightIndex(-1); setShowArticleDropdown((prev) => !prev) }} className="absolute right-2 text-gray-500 hover:text-gray-700">
                              <ChevronDown className="w-4 h-4" />
                            </button>
                          </div>
                          {showArticleDropdown && (
                            <div className="absolute z-20 w-full top-full mt-1 bg-white border border-gray-300 rounded-input shadow-lg overflow-y-auto max-h-32">
                              {filteredArticleTypes.length === 0 ? (
                                <div className="px-3 py-2 text-xs text-gray-400 text-center">No article types found</div>
                              ) : (
                                filteredArticleTypes.map((type, idx) => (
                                  <div
                                    key={type}
                                    onMouseEnter={() => setArticleHighlightIndex(idx)}
                                    onMouseDown={() => selectArticleType(type)}
                                    className={`px-3 py-1.5 cursor-pointer text-sm border-b border-gray-100 last:border-b-0 uppercase ${idx === articleHighlightIndex ? "bg-brand-light text-brand font-medium" : "hover:bg-blue-50"}`}
                                  >
                                    {type}
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col">
                          <label className={LABEL_CLS}>Number of Articles <span className="text-red-500">*</span></label>
                          <input type="number" name="no_of_articles" value={formData.no_of_articles} onChange={handleChange} onKeyDown={handleEnterAsTab} min="1" required className={INPUT_CLS} />
                        </div>
                        <div className="flex flex-col">
                          <label className={LABEL_CLS}>Article Rate <span className="text-red-500">*</span></label>
                          <input type="text" name="article_amount" value={formData.article_amount} onChange={handleChange} onKeyDown={(e) => handleFieldEnter(e, "handling_charges")} required className={INPUT_CLS} />
                        </div>
                      </div>
                      {showShipmentExtra && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2 pt-2 border-t border-gray-100">
                          <div className="flex flex-col sm:col-span-3">
                            <label className={LABEL_CLS}>Parcel Details</label>
                            <textarea name="parcel_detail" value={formData.parcel_detail} onChange={handleChange} onKeyDown={handleEnterAsTab} rows="1" className={`${INPUT_CLS} resize-none`} />
                          </div>
                          <div className="flex flex-col">
                            <label className={LABEL_CLS}>Weight (kg)</label>
                            <input type="text" name="weight" value={formData.weight} onChange={handleChange} onKeyDown={handleEnterAsTab} className={INPUT_CLS} />
                          </div>
                          <div className="flex flex-col">
                            <label className={LABEL_CLS}>Shipment Value</label>
                            <input type="text" name="shipment_value" value={formData.shipment_value} onChange={handleChange} onKeyDown={handleEnterAsTab} className={INPUT_CLS} />
                          </div>
                          <div className="flex flex-col">
                            <label className={LABEL_CLS}>Parcel Image 1</label>
                            <div className="border border-dashed border-gray-300 rounded-input p-1.5 text-center hover:border-brand cursor-pointer">
                              <input type="file" name="parcel_image_1" onChange={handleImageChange} accept="image/*" capture="environment" className="hidden" id="image1Input" />
                              <label htmlFor="image1Input" className="cursor-pointer block">{imagePreview.img1 ? <img src={imagePreview.img1} alt="Preview 1" className="w-full h-12 object-contain" /> : <p className="text-xs text-gray-500 py-3">Upload</p>}</label>
                            </div>
                          </div>
                          <div className="flex flex-col">
                            <label className={LABEL_CLS}>Parcel Image 2</label>
                            <div className="border border-dashed border-gray-300 rounded-input p-1.5 text-center hover:border-brand cursor-pointer">
                              <input type="file" name="parcel_image_2" onChange={handleImageChange} accept="image/*" capture="environment" className="hidden" id="image2Input" />
                              <label htmlFor="image2Input" className="cursor-pointer block">{imagePreview.img2 ? <img src={imagePreview.img2} alt="Preview 2" className="w-full h-12 object-contain" /> : <p className="text-xs text-gray-500 py-3">Upload</p>}</label>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="pt-2 mt-2 border-t border-gray-200">
                        <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                          <h3 className="text-sm font-semibold text-secondary">Shipment Charges</h3>
                          <div className="flex items-center gap-2">
                            <button type="button" tabIndex={-1} onClick={() => setShowGSTDetails(!showGSTDetails)} className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-input transition">
                              GST {showGSTDetails ? "▴" : "▾"}
                            </button>
                            <button type="button" tabIndex={-1} onClick={() => setShowAdditionalCharges(!showAdditionalCharges)} className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-brand bg-brand-light hover:bg-blue-100 border border-blue-200 rounded-input transition">
                              Additional {showAdditionalCharges ? "▴" : "▾"}
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                          <div className="flex flex-col">
                            <label className="block text-xs font-medium text-secondary mb-1">Freight Charges</label>
                            <input type="text" value={calculateFreightCharges()} disabled className="px-3 py-1.5 text-sm border border-gray-300 rounded-input bg-gray-100 text-gray-600 font-semibold cursor-not-allowed" />
                          </div>
                          <div className="flex flex-col">
                            <label className="block text-xs font-medium text-secondary mb-1">Handling Charges</label>
                            <input type="text" name="handling_charges" value={formData.handling_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 py-1.5 text-sm border border-gray-300 rounded-input focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
                          </div>
                          <div className="flex flex-col">
                            <label className="block text-xs font-medium text-secondary mb-1">Other Charges</label>
                            <input type="text" name="other_charges" value={formData.other_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 py-1.5 text-sm border border-gray-300 rounded-input focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
                          </div>
                        </div>
                        {showGSTDetails && (
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 border border-gray-200 rounded-input mb-3">
                            <div className="flex flex-col">
                              <label className="block text-xs font-medium text-secondary mb-1">SGST %</label>
                              <input type="text" name="sgst_percentage" value={formData.sgst_percentage} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 py-1.5 text-sm border border-gray-300 rounded-input focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
                            </div>
                            <div className="flex flex-col">
                              <label className="block text-xs font-medium text-secondary mb-1">CGST %</label>
                              <input type="text" name="cgst_percentage" value={formData.cgst_percentage} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 py-1.5 text-sm border border-gray-300 rounded-input focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
                            </div>
                            <div className="flex flex-col">
                              <label className="block text-xs font-medium text-secondary mb-1">IGST %</label>
                              <input type="text" name="igst_percentage" value={formData.igst_percentage} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 py-1.5 text-sm border border-gray-300 rounded-input focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
                            </div>
                            <div className="bg-purple-50 p-2 rounded-input"><p className="text-[10px] text-gray-600 font-semibold">SGST</p><p className="text-sm font-bold text-purple-600">₹{calculateGSTAmounts().sgst}</p></div>
                            <div className="bg-purple-50 p-2 rounded-input"><p className="text-[10px] text-gray-600 font-semibold">CGST</p><p className="text-sm font-bold text-purple-600">₹{calculateGSTAmounts().cgst}</p></div>
                            <div className="bg-purple-50 p-2 rounded-input"><p className="text-[10px] text-gray-600 font-semibold">IGST</p><p className="text-sm font-bold text-purple-600">₹{calculateGSTAmounts().igst}</p></div>
                          </div>
                        )}
                        {showAdditionalCharges && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-3 border border-gray-200 rounded-input mb-3">
                            <div className="flex flex-col"><label className="block text-xs font-medium text-secondary mb-1">Door Pickup</label><input type="text" name="door_pickup_charges" value={formData.door_pickup_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 py-1.5 text-sm border border-gray-300 rounded-input focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" /></div>
                            <div className="flex flex-col"><label className="block text-xs font-medium text-secondary mb-1">Door Delivery</label><input type="text" name="delivery_charges" value={formData.delivery_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 py-1.5 text-sm border border-gray-300 rounded-input focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" /></div>
                            <div className="flex flex-col"><label className="block text-xs font-medium text-secondary mb-1">Docket Fee</label><input type="text" name="docket_fee" value={formData.docket_fee} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 py-1.5 text-sm border border-gray-300 rounded-input focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" /></div>
                            <div className="flex flex-col"><label className="block text-xs font-medium text-secondary mb-1">Discount</label><input type="text" name="discount" value={formData.discount} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 py-1.5 text-sm border border-gray-300 rounded-input focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" /></div>
                          </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="flex flex-col">
                            <label className="block text-xs font-medium text-secondary mb-1">Total Amount</label>
                            <div className="px-3 py-1.5 text-sm border border-gray-300 rounded-input bg-gray-100 font-bold text-green-600">₹{calculateTotalAmount()}</div>
                          </div>
                          <div className="flex flex-col">
                            <label className="block text-xs font-medium text-secondary mb-1">Payment Status</label>
                            <select name="mode_of_payment" value={formData.mode_of_payment} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 py-1.5 text-sm border border-gray-300 rounded-input focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20">
                              <option value="to_pay">To Pay</option>
                              <option value="credit">Credit</option>
                              <option value="paid">Paid</option>
                              <option value="foc">FOC (Free of Charge)</option>
                            </select>
                          </div>
                          {formData.mode_of_payment !== "to_pay" && formData.mode_of_payment !== "credit" && formData.mode_of_payment !== "foc" && (
                            <div className="flex flex-col">
                              <label className="block text-xs font-medium text-secondary mb-1">Payment Mode</label>
                              <select name="payment_mode" value={formData.payment_mode} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 py-1.5 text-sm border border-gray-300 rounded-input focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20">
                                <option value="">Select Mode</option>
                                <option value="cash">Cash</option>
                                <option value="upi">UPI</option>
                                <option value="bank_transfer">Bank Transfer</option>
                                <option value="cheque">Cheque</option>
                                <option value="online">Online</option>
                              </select>
                            </div>
                          )}
                          {(formData.mode_of_payment === "paid" || formData.mode_of_payment === "paid_balance") && (
                            <div className="flex flex-col">
                              <label className="block text-xs font-medium text-secondary mb-1">Paid Amount</label>
                              <input type="text" name="paid_amount" value={formData.paid_amount} onChange={handleChange} onKeyDown={handleEnterAsTab} className="px-3 py-1.5 text-sm border border-gray-300 rounded-input focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
                            </div>
                          )}
                        </div>
                        {(formData.mode_of_payment === "paid" || formData.mode_of_payment === "paid_balance") && (
                          <div className="mt-3 grid grid-cols-2 gap-2">
                            <div className="bg-brand-light p-2 rounded-input"><p className="text-[10px] text-gray-600 font-semibold">Paid</p><p className="text-sm font-bold text-brand">₹{formData.paid_amount || "0.00"}</p></div>
                            <div className="bg-gray-50 p-2 rounded-input"><p className="text-[10px] text-gray-600 font-semibold">Balance Due</p><p className="text-sm font-bold text-brand">₹{calculateBalanceAmount()}</p></div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
      </form>

      <div className="fixed bottom-4 right-4 z-40 lg:right-8">
        <Button variant="primary" type="submit" form="add-shipment-form" loading={loading} disabled={loading} onKeyDown={handleEnterAsTab}>
          {loading ? "Creating..." : "Create Shipment"}
        </Button>
      </div>

      {showEwayBillModal && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
    <div className="bg-white rounded-sm shadow-lg w-[95%] max-w-md">

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-brand text-white rounded-t-sm">
        <h3 className="text-sm font-semibold">Add E-WayBill</h3>
        <button onClick={handleCloseEwayBillModal}>✕</button>
      </div>

      {/* Body */}
      <div className="p-4 space-y-3">

        {ewayBills.map((value, index) => {
          const isLast = index === ewayBills.length - 1
          const isValid = isValidEway(value)

          return (
            <div key={index}>
              <input
                type="text"
                value={value}
                maxLength={12}
                inputMode="numeric"
                placeholder="Enter 12-digit E-Way Bill No."
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "")
                  const updated = [...ewayBills]
                  updated[index] = val
                  setEwayBills(updated)
                }}
                className={`w-full px-3 py-2 border rounded-sm
                  ${value && !isValid ? "border-red-500" : "border-gray-300"}
                  focus:outline-none`}
              />

              {value && !isValid && (
                <p className="text-xs text-red-500 mt-1">
                  E-Way Bill must be exactly 12 digits
                </p>
              )}
            </div>
          )
        })}

        {/* Buttons */}
        <div className="flex gap-2 pt-2">
          <button
            disabled={!isValidEway(ewayBills[ewayBills.length - 1])}
            onClick={() => setEwayBills([...ewayBills, ""])}
            className={`px-3 py-2 text-sm rounded-sm text-white
              ${isValidEway(ewayBills[ewayBills.length - 1])
                ? "bg-sky-500 hover:bg-sky-600"
                : "bg-gray-300 cursor-not-allowed"}`}
          >
            Add New
          </button>

          <button
            disabled={ewayBills.length === 1}
            onClick={() => setEwayBills(ewayBills.slice(0, -1))}
            className={`px-3 py-2 text-sm rounded-sm text-white
              ${ewayBills.length > 1
                ? "bg-red-500 hover:bg-red-600"
                : "bg-gray-300 cursor-not-allowed"}`}
          >
            Remove Last
          </button>

          <button
            onClick={handleSubmitEwayBills}
            disabled={!ewayBills.some(isValidEway)}
            className={`px-3 py-2 text-sm rounded-sm text-white
              ${ewayBills.some(isValidEway)
                ? "bg-green-500 hover:bg-green-600"
                : "bg-gray-300 cursor-not-allowed"}`}
          >
            Submit
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end px-4 py-2 border-t">
              <button
                onClick={handleCloseEwayBillModal}
                className="px-3 py-1.5 text-sm border rounded-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showViewEwayBillModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-sm shadow-lg w-[95%] max-w-md">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2 bg-green-600 text-white rounded-t-sm">
              <h3 className="text-sm font-semibold">Submitted E-Way Bills ({submittedEwayBills.length})</h3>
              <button onClick={() => setShowViewEwayBillModal(false)}>✕</button>
            </div>

            {/* Body */}
            <div className="p-4">
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {submittedEwayBills.map((bill, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-sm"
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex items-center justify-center w-6 h-6 bg-green-600 text-white text-xs font-bold rounded-full">
                        {index + 1}
                      </span>
                      <span className="text-sm font-semibold text-gray-800">{bill}</span>
                    </div>
                    <button
                      onClick={() => handleRemoveEwayBill(index)}
                      className="px-2 py-1 text-xs bg-red-500 hover:bg-red-600 text-white rounded-sm transition"
                      title="Remove this E-way bill"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-between px-4 py-2 border-t">
              <button
                onClick={() => {
                  setShowViewEwayBillModal(false)
                  setShowEwayBillModal(true)
                }}
                className="px-3 py-1.5 text-sm bg-brand hover:bg-brand text-white rounded-sm"
              >
                Add More
              </button>
              <button
                onClick={() => setShowViewEwayBillModal(false)}
                className="px-3 py-1.5 text-sm border rounded-sm hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  )
}

export default AddShipment;